import _ from 'lodash';
import { Fragment, ReactElement } from 'react';

import { Language } from '@breathelife/types';
import {
  PdfDocumentTitle,
  PdfFieldGroupHeader,
  PdfFieldText,
  PdfLabelText,
  PdfPaddedGrid,
  PdfPaddedGridItem,
  PdfQuestionnaireSectionHeader,
  PdfSubsectionHeader,
  ReactHtmlParser,
} from '@breathelife/ui-components';
import { Box, Grid } from '@mui/material';
import {
  FieldTypes,
  getAllSections,
  RenderingField,
  RenderingFieldGroup,
  RenderingOptionField,
} from '@zinnia/form-engine-sdk';

import usePdfContext from '../../../../Context/usePdfContext';
import { formatTextFieldValue, isUnansweredField } from '../../../../helpers/fieldHelpers';
import { translate } from '../../../../Localization/Localizer';
import { FieldChunk, FieldContainer } from '../../QuestionnaireVariants/QuestionnaireVariantsA/FieldGroups';
import { QuestionAnswerGroupA } from './Styles/PdfQuestionAnswerGroupA';

export function MedicalInterviewAnswers(): ReactElement | null {
  const { participantWithMedicalInterviewAnswers, application, currency } = usePdfContext();
  if (!participantWithMedicalInterviewAnswers) return null;
  const locale = application.lang;
  const sections = getAllSections(participantWithMedicalInterviewAnswers.answers);

  return (
    <Fragment>
      <Box mb={5}>
        <PdfDocumentTitle>{translate('medicalInterviewAnswers.title', { locale })}</PdfDocumentTitle>
        <PdfFieldText>
          {translate('medicalInterviewAnswers.text', {
            locale,
            fullName: participantWithMedicalInterviewAnswers.fullName,
          })}
        </PdfFieldText>
      </Box>
      {sections.map((section) => {
        const { title: sectionTitle, id, subsections } = section;

        return (
          <div key={id}>
            <Box mb={5} sx={{ pageBreakInside: 'avoid' }}>
              <PdfQuestionnaireSectionHeader>
                <ReactHtmlParser html={sectionTitle} />
              </PdfQuestionnaireSectionHeader>

              {subsections.map((subsection) => {
                const {
                  title: subsectionTitle,
                  text: subsectionText,
                  visible,
                  id,
                  fieldGroups: fieldGroups,
                } = subsection;

                if (!visible) return null;
                return (
                  <div key={id}>
                    <Box mb={1} mt={2}>
                      <PdfSubsectionHeader>
                        <ReactHtmlParser html={subsectionTitle} />
                      </PdfSubsectionHeader>
                      {subsectionText && (
                        <Box mb={2} mt={-1}>
                          <PdfFieldText>
                            <ReactHtmlParser html={subsectionText} />
                          </PdfFieldText>
                        </Box>
                      )}
                    </Box>
                    {fieldGroups.map((fieldGroup: RenderingFieldGroup) => {
                      const { title: fieldGroupTitle, text: fieldGroupText, id, visible, fields } = fieldGroup;
                      if (!visible) return null;
                      const filteredFields = fields.filter((field) => {
                        if (!field.readOnly && (!field.visible || isUnansweredField(field))) {
                          return false;
                        }
                        return true;
                      });
                      const fieldChunks = _.chunk(filteredFields, 1);

                      return (
                        <Fragment key={id}>
                          <FieldContainer key={id} pageBreakInside={'auto'}>
                            {fieldGroupTitle && (
                              <Box mb={1}>
                                <PdfFieldGroupHeader>
                                  <ReactHtmlParser html={fieldGroupTitle} />
                                </PdfFieldGroupHeader>
                              </Box>
                            )}
                            {fieldGroupText && (
                              <Box mb={2} mt={-1}>
                                <PdfLabelText>
                                  <ReactHtmlParser html={fieldGroupText} />
                                </PdfLabelText>
                              </Box>
                            )}
                            {fieldChunks.map((fieldChunk, index) => {
                              const hasCheckboxGroup = fieldChunk.some(
                                (field: RenderingField) => field.type === FieldTypes.checkboxGroup,
                              );
                              const Wrapper = hasCheckboxGroup ? Box : FieldChunk;
                              return (
                                <Wrapper mb={2} key={id + `_` + index}>
                                  <PdfPaddedGrid container spacing={3} alignItems={'flex-end'}>
                                    {fieldChunk.map((field: RenderingField) => (
                                      <Fragment key={field.id}>
                                        <Field field={field} locale={locale} currency={currency} />
                                      </Fragment>
                                    ))}
                                  </PdfPaddedGrid>
                                </Wrapper>
                              );
                            })}
                          </FieldContainer>
                        </Fragment>
                      );
                    })}
                  </div>
                );
              })}
            </Box>
          </div>
        );
      })}
    </Fragment>
  );
}

function Field(props: { field: RenderingField; locale: Language; currency: string }): ReactElement | null {
  let fieldElement;
  const options = (props.field as RenderingOptionField).options;
  switch (props.field.type) {
    case FieldTypes.radio:
      fieldElement = (
        <PdfPaddedGridItem item xs={12}>
          {props.field?.title && (
            <PdfFieldGroupHeader>
              <ReactHtmlParser html={props.field.title} />
            </PdfFieldGroupHeader>
          )}
          {props.field?.text && (
            <PdfFieldText>
              <ReactHtmlParser html={props.field.text} />
            </PdfFieldText>
          )}
          <Grid xs={6}>
            {options.map((option) => {
              const selectedOption = props.field?.value === option.text;
              return (
                <QuestionAnswerGroupA
                  key={option.id}
                  value={option.text}
                  isOptionGroup
                  isUnselectedOption={!selectedOption}
                />
              );
            })}
          </Grid>
        </PdfPaddedGridItem>
      );
      break;
    case FieldTypes.textarea:
      fieldElement = (
        <PdfPaddedGridItem item xs={12}>
          {props.field?.title && (
            <PdfFieldGroupHeader>
              <ReactHtmlParser html={props.field.title} />
            </PdfFieldGroupHeader>
          )}
          {props.field?.text && (
            <PdfFieldText>
              <ReactHtmlParser html={props.field.text} />
            </PdfFieldText>
          )}
          <QuestionAnswerGroupA
            value={formatTextFieldValue(props.field.value, props.field.type, props.locale, props.currency)}
            withLineBreaks={true}
          />
        </PdfPaddedGridItem>
      );
      break;
    case FieldTypes.checkboxGroup:
      fieldElement = (
        <PdfPaddedGridItem item xs={12}>
          {props.field?.title && (
            <PdfFieldGroupHeader>
              <ReactHtmlParser html={props.field.title} />
            </PdfFieldGroupHeader>
          )}
          {props.field?.text && (
            <PdfFieldText>
              <ReactHtmlParser html={props.field.text} />
            </PdfFieldText>
          )}
          <Grid xs={6}>
            {options.map((option) => {
              const isSelected = Array.isArray(props.field.value) && props.field.value.includes(option.text);
              return (
                <QuestionAnswerGroupA
                  key={option.id}
                  value={option.text}
                  isOptionGroup
                  isUnselectedOption={!isSelected}
                />
              );
            })}
          </Grid>
        </PdfPaddedGridItem>
      );
      break;
    case FieldTypes.input:
    default:
      fieldElement = (
        <PdfPaddedGridItem item xs={12}>
          {props.field?.title && (
            <PdfFieldGroupHeader>
              <ReactHtmlParser html={props.field.title} />
            </PdfFieldGroupHeader>
          )}
          {props.field?.text && (
            <PdfFieldText>
              <ReactHtmlParser html={props.field.text} />
            </PdfFieldText>
          )}
          <QuestionAnswerGroupA
            value={formatTextFieldValue(
              props.field.value,
              props.field.type,
              props.locale,
              props.currency,
              (props.field as RenderingOptionField).options,
            )}
          />
        </PdfPaddedGridItem>
      );
      break;
  }

  return fieldElement;
}
